import { BaseDriver } from './device'
import type { FnFactoryResetProvider } from './fn_factory_reset'
import { bytesPadding64, delay, numberToRgb, printHex } from '@libs/utils/tools'
import { musicData } from '@libs/music/music'
import { ResDeviceState } from '@libs/types/response'
import { mg63Magnet } from './magent/mg63'
import { DeviceProtocol } from '@hid/device/device_model'
import { MusicCaptureStatus, SpecificItem } from '@libs/types/enum'
import { decodeLedParameterRam, LED_PARAMETER_RAM_SIZE, LedParameterRam, LightProfileParams, LOGO_LED_MODE_COUNT, MAIN_LED_MODE_COUNT, MG63KeyboardParams, mg63ReportMap } from './data_conversion/mg63'
import {
  getHuochairenDefaultFnVk,
  getHuochairenFnActionKeyByRawCode,
  getHuochairenFnMatrixIndexToKKMap,
  getHuochairenFnVkByDvk,
  getHuochairenRawCodeByActionKey,
  getHuochairenSupportedDeviceFunctionActionKeys,
  hasHuochairenDefaultFnLayout,
} from './data_conversion/registry/huochairen'
import { KIO, KK, Layout, MacroType, OutputMode, Record, SetMagnetSenior, VK } from '@hid/define/define'
import { allKeysForDevice, DVKFromVK, HIDFromVk, kkFromVK, VKFromHID, vkFromKK } from '@hid/define/define_fn'
import { deviceManager } from '@hid/device/device_manager'
import { defaultVK, FNVK } from '@hid/define/define_keys'
import defineType from '@hid/define/define_type'
import { lightMatrix } from '@hid/define/define_light_matrix'
import { isDeviceFunctionActionKey } from '@/define/internal_action_keys'
import { deviceMap } from '@hid/define/define_list'
import { compare } from 'compare-versions'
import { fakeSocket } from '@hid/fakesocket/fakesocket'
import { DriverSocketMsgType } from '@libs/types/message'
import { dataProfileManage } from '@hid/snapshot/profile'
import firmware from '@/upgrade/firmware'

interface MacroConfig {
  actionCount: number
  repeatCount: number
  macroType: number
}

interface DeviceMacroBinding {
  offset: number
  repeatCount: number
  macroRawType: number
}

type DecodedLayerKey = { type: 'key'; vk: VK } | { type: 'macro'; binding: DeviceMacroBinding }
type LayerLayoutOverlay = Pick<Layout, 'KeyMap' | 'KeyMapModifier' | 'KeyMapFire' | 'Macros' | 'MacroType'>

const isHuochairenMacroBindingType = (type: number) => type >= 0x80 && type <= 0x84
const HUOCHAIREN_V120_MIN_VERSION = '1.20'
const HUOCHAIREN_ONBOARD_CONFIG_MIN_VERSION = HUOCHAIREN_V120_MIN_VERSION
const HUOCHAIREN_CANCEL_DEADZONE_V120_MIN_VERSION = HUOCHAIREN_V120_MIN_VERSION
const HUOCHAIREN_CANCEL_DEADZONE_DEFAULT_VALUE = 30
const HUOCHAIREN_ONBOARD_CONFIG_COUNT = 4
const HUOCHAIREN_ONBOARD_SWITCH_READ_START = 0xe0
const HUOCHAIREN_ONBOARD_SWITCH_READ_END = 0xe4
const HUOCHAIREN_ONBOARD_SWITCH_READ_TIMEOUT = 1500
const HUOCHAIREN_ONBOARD_CONFIG_RESPONSE_RETRY_COUNT = 6
const HUOCHAIREN_ONBOARD_CONFIG_RESPONSE_RETRY_DELAY = 60

const getCurrentBoundOnboardConfigIndex = () => {
  const cfgList = dataProfileManage().CfgList()
  const currentProfile = cfgList.List[cfgList.Current]
  return typeof currentProfile?.slotIndex === 'number' ? currentProfile.slotIndex : typeof currentProfile?.OnboardSlot === 'number' ? currentProfile.OnboardSlot : null
}

export class mg63Driver extends BaseDriver {
  musicIns: musicData
  private customMode = 0xf1 // 自定义模式 0xf1 ~ 0xf4
  declare magnet: mg63Magnet | undefined
  public isBusy = false
  private musicFrameInFlight = false
  private trafficProtectionDepth = 0
  private shouldResumeMusicAfterTraffic = false
  private cancelDefaultDeadZoneV120Promise: Promise<boolean> | null = null
  private isNewProtocolCache: boolean | null = null
  // 所有 0xE1 读取操作共用此串行锁，防止并发读取互相抢占响应（cross-talk）
  private _e1ReadLock: Promise<void> = Promise.resolve()

  private serializedE1Read<T>(fn: () => Promise<T>): Promise<T> {
    const result = this._e1ReadLock.then(() => fn())
    // 将锁链接到当前操作，无论成功/失败都释放
    this._e1ReadLock = result.then(
      () => {},
      () => {},
    )
    return result
  }
  private onboardConfigSupportPromise: Promise<boolean> | null = null
  private onboardConfigSupportCache: boolean | null = null
  private onboardConfigCurrentIndex: number | null = null
  // 并发读取去重：缓存为 null 时若已有一次 HID 读取正在进行，后续调用复用同一 Promise
  private _currentIndexReadInFlight: Promise<number> | null = null
  // 并发写入去重：多处同时调用 ensureBoundOnboardConfig(force=false) 时，只执行一次槽位对齐操作
  private _ensureBoundInFlight: Promise<number | null> | null = null
  private onboardSwitchReadWaitResolve: (() => void) | null = null
  private onboardSwitchReadWaitTimer: ReturnType<typeof setTimeout> | null = null
  constructor(public protocol: DeviceProtocol) {
    super(protocol)
    this.musicIns = new musicData(this.onMusicData)
    this.magnet = new mg63Magnet(protocol, this)
    void this.initializeOnboardConfigListeners()
  }

  private async initializeOnboardConfigListeners() {
    if (!(await this.isOnboardConfigSupportedByFirmware())) {
      return
    }

    this.startOnboardConfigMonitor(async (currentIndex) => {
      await this.handleOnboardConfigSwitch(currentIndex)
    })
    this.DeviceHID.onReciveAny((data) => {
      this.handleOnboardSwitchReadReport(data)

      const currentIndex = this.extractOnboardConfigIndexFromActiveReport(data)
      if (currentIndex == null) {
        return
      }

      void this.notifyOnboardConfigSwitch(currentIndex, async (nextIndex) => {
        await this.waitForOnboardSwitchReadCompletion()
        await this.handleOnboardConfigSwitch(nextIndex)
      })
    })
  }

  private clearOnboardSwitchReadWaitTimer() {
    if (this.onboardSwitchReadWaitTimer != null) {
      clearTimeout(this.onboardSwitchReadWaitTimer)
      this.onboardSwitchReadWaitTimer = null
    }
  }

  private finishOnboardSwitchReadWait() {
    const resolve = this.onboardSwitchReadWaitResolve
    this.onboardSwitchReadWaitResolve = null
    this.clearOnboardSwitchReadWaitTimer()
    resolve?.()
  }

  private refreshOnboardSwitchReadWaitTimer() {
    if (this.onboardSwitchReadWaitResolve == null) {
      return
    }

    this.clearOnboardSwitchReadWaitTimer()
    this.onboardSwitchReadWaitTimer = setTimeout(() => {
      this.finishOnboardSwitchReadWait()
    }, HUOCHAIREN_ONBOARD_SWITCH_READ_TIMEOUT)
  }

  private handleOnboardSwitchReadReport(data: number[]) {
    if (this.onboardSwitchReadWaitResolve == null) {
      return
    }
    const reportType = data[0] ?? -1
    if (reportType < HUOCHAIREN_ONBOARD_SWITCH_READ_START || reportType > HUOCHAIREN_ONBOARD_SWITCH_READ_END) {
      return
    }
    // 最后一包数据的第4个字节会是0x05，暂时用这个特征来判断是否是完整的读取完成报告
    if (data[3] != 0x05) {
      return
    }
    if (reportType === HUOCHAIREN_ONBOARD_SWITCH_READ_END) {
      this.finishOnboardSwitchReadWait()
      return
    }

    this.refreshOnboardSwitchReadWaitTimer()
  }

  private waitForOnboardSwitchReadCompletion(): Promise<void> {
    this.finishOnboardSwitchReadWait()

    return new Promise((resolve) => {
      this.onboardSwitchReadWaitResolve = resolve
      this.refreshOnboardSwitchReadWaitTimer()
    })
  }

  private getValidOnboardConfigIndex(index: number): number | null {
    return index >= 0 && index < HUOCHAIREN_ONBOARD_CONFIG_COUNT ? index : null
  }

  private normalizeOnboardConfigIndex(protocolIndex: number): number | null {
    // 固件协议使用 0-based（0-3），与内部索引一致，直接校验范围
    return this.getValidOnboardConfigIndex(protocolIndex)
  }

  private async handleOnboardConfigSwitch(currentIndex: number) {
    this.onboardConfigCurrentIndex = currentIndex
    this.rememberOnboardConfigIndex(currentIndex)

    const linkedProfileIndex = dataProfileManage().getProfileIndexBySlot(currentIndex)
    const configData = linkedProfileIndex !== null ? dataProfileManage().CfgSwitch({ Value: linkedProfileIndex }) : dataProfileManage().CfgList()
    fakeSocket.sendMsgData(DriverSocketMsgType.MSDeviceSwitchSnapshotEnd, configData)
  }

  private async isOnboardConfigSupportedByFirmware(): Promise<boolean> {
    if (this.onboardConfigSupportCache != null) {
      return this.onboardConfigSupportCache
    }

    if (this.onboardConfigSupportPromise == null) {
      this.onboardConfigSupportPromise = this.getVersionInfo()
        .then(({ version, isNewProtocol }) => isNewProtocol || compare(version, HUOCHAIREN_ONBOARD_CONFIG_MIN_VERSION, '>='))
        .catch((error) => {
          console.warn('mg63Driver: failed to get firmware version for onboard config support', error)
          return false
        })
        .then((supported) => {
          this.onboardConfigSupportCache = supported
          return supported
        })
    }

    return this.onboardConfigSupportPromise
  }

  private async ensureOnboardConfigSupported() {
    if (!(await this.isOnboardConfigSupportedByFirmware())) {
      throw new Error(`huochairen onboard config requires firmware >= ${HUOCHAIREN_ONBOARD_CONFIG_MIN_VERSION}`)
    }
  }

  private async sendOnboardConfigCommand(isWrite: boolean, index?: number) {
    const data = Array(64).fill(0)
    data[0] = 0x51
    data[1] = isWrite ? 0x00 : 0x01
    if (isWrite) {
      data[2] = index ?? 0
    }

    let lastResponse: number[] = []
    for (let attempt = 0; attempt < HUOCHAIREN_ONBOARD_CONFIG_RESPONSE_RETRY_COUNT; attempt++) {
      const response = await this.DeviceHID.send(data)
      lastResponse = response
      // 写入命令：固件响应的 data[2] 是状态码而非槽位索引，只需确认 CMD 字节匹配即可
      // 读取命令：需要额外验证 data[2] 是合法的槽位索引（0-3）
      const isValid = isWrite ? this.isWriteOnboardConfigResponse(response) : this.isReadOnboardConfigResponse(response)
      if (isValid) {
        return response
      }
      await delay(HUOCHAIREN_ONBOARD_CONFIG_RESPONSE_RETRY_DELAY)
    }

    return lastResponse
  }

  /**
   * 通过 0xE1 读取 profile 配置文件中 ConfigNum_OFFSET(=1) 字节，
   * 该字节由固件维护为"键盘当前所在板载配置编号"。
   *
   * 相比 0x51（设置/获取当前配置编号）走读取分支偶现返回旧值的问题，
   * 0xE1 读 profile 字节由固件直接落盘维护，连接驱动后可拿到真实当前槽位。
   * 响应格式：CMD + 配置编号 + 偏移量 + 保留 + 有效数据，data[3]=偏移量，
   * 因此 ConfigNum 位于 res[4]（与既有 readSpecificItem 的 res[OFFSET] 模式一致）。
   */
  private async readCurrentOnboardConfigIndexFromProfile(): Promise<number | null> {
    const data = Array(64).fill(0)
    data[0] = 0xe1
    data[1] = 0x00 // 配置编号：固件中 ConfigNum 为全局当前槽位，任意 profile 读到的都是同一个值
    data[3] = MG63KeyboardParams.ConfigNum_OFFSET

    return this.serializedE1Read(async () => {
      for (let attempt = 0; attempt < HUOCHAIREN_ONBOARD_CONFIG_RESPONSE_RETRY_COUNT; attempt++) {
        const res = await this.DeviceHID.send(data)
        if ((res[0] ?? -1) === 0xe1) {
          const protocolIndex = res[4] ?? 0
          const normalized = this.normalizeOnboardConfigIndex(protocolIndex)
          if (normalized !== null) {
            return normalized
          }
        }
        await delay(HUOCHAIREN_ONBOARD_CONFIG_RESPONSE_RETRY_DELAY)
      }
      return null
    })
  }

  // 写入响应：只校验 CMD 字节（固件 data[2] 为状态码，不含槽位信息）
  private isWriteOnboardConfigResponse(data: number[]): boolean {
    return (data[0] ?? -1) === 0x51
  }

  // 读取响应：校验 CMD 字节 + data[2] 为合法槽位索引
  private isReadOnboardConfigResponse(data: number[]): boolean {
    if ((data[0] ?? -1) !== 0x51) {
      return false
    }
    return this.normalizeOnboardConfigIndex(data[2] ?? -1) !== null
  }

  private extractOnboardConfigIndex(data: number[]): number | null {
    const protocolIndex = data[2] ?? -1
    return this.normalizeOnboardConfigIndex(protocolIndex)
  }

  private extractOnboardConfigIndexFromActiveReport(data: number[]): number | null {
    if (data[0] !== 0x70) {
      return null
    }
    //  byte3:是否恢复了出厂设置
    if (data[3] !== 0x00) {
      return null
    }
    const protocolIndex = data[1] ?? -1
    return this.normalizeOnboardConfigIndex(protocolIndex)
  }

  private onMusicData = async (data: number[]) => {
    // computeFPS()
    if (this.isBusy || this.musicFrameInFlight || this.protocol.hasPendingResponseTraffic()) return
    if (this.save.ColorsDynamic.Run == false) {
      return
    }
    if (data.length > 0) {
      await this.sendEffectData(data)
    }
  }

  private async beginHighTrafficProtection() {
    if (this.trafficProtectionDepth === 0) {
      this.isBusy = true
      const musicStatus = this.musicIns.getMusicCaptureStatus()
      this.shouldResumeMusicAfterTraffic = musicStatus === MusicCaptureStatus.Start || musicStatus === MusicCaptureStatus.Continue
      if (this.shouldResumeMusicAfterTraffic) {
        await this.musicIns.musicCapturePause()
      }
    }

    this.trafficProtectionDepth++
  }

  private async endHighTrafficProtection(quietWindowMs: number = 0) {
    if (this.trafficProtectionDepth === 0) {
      return
    }

    this.trafficProtectionDepth--
    if (this.trafficProtectionDepth > 0) {
      return
    }

    try {
      if (quietWindowMs > 0) {
        await delay(quietWindowMs)
      }

      const shouldResume = this.shouldResumeMusicAfterTraffic
      this.shouldResumeMusicAfterTraffic = false
      if (shouldResume) {
        await this.musicIns.musicCaptureContinue()
      }
    } finally {
      this.isBusy = false
    }
  }

  private async withHighTrafficProtection<T>(task: () => Promise<T>, quietWindowMs: number = 0): Promise<T> {
    await this.beginHighTrafficProtection()
    try {
      return await task()
    } finally {
      await this.endHighTrafficProtection(quietWindowMs)
    }
  }

  async withMusicCapturePausedForRead<T>(task: () => Promise<T>): Promise<T> {
    return await this.withHighTrafficProtection(task)
  }

  private async withMusicCapturePausedForWrite<T>(task: () => Promise<T>, quietWindowMs: number = 180): Promise<T> {
    return await this.withHighTrafficProtection(task, quietWindowMs)
  }

  private async sendEffectData(data: number[]) {
    const keyMap = lightMatrix(deviceManager.deviceID)
    const packets: number[][] = []
    let currentPacket: number[] = [0xb5]

    // 构建数据包
    for (let i = 0; i < keyMap.size; i++) {
      const k = keyMap.get(i)
      if (k === undefined || k === 0) {
        continue
      }

      // 检查当前包是否已满 (Key + 3 Colors = 4 bytes)
      if (currentPacket.length + 4 > 64) {
        packets.push(currentPacket)
        currentPacket = [0xb5]
      }

      // 颜色格式: Key, R, G, B
      currentPacket.push(k, data[i * 3], data[i * 3 + 1], data[i * 3 + 2])
    }
    if (currentPacket.length > 1) {
      packets.push(currentPacket)
    }

    if (packets.length === 0) {
      return
    }

    // 发送数据包
    this.musicFrameInFlight = true
    try {
      await Promise.all(
        packets.map((packet) => {
          const toSend = bytesPadding64(packet, 64, [0x00])
          // 音乐帧属于低优先级流量，只等待数据真正送入 HID，不等待设备回包。
          return this.DeviceHID.send(toSend, false, false)
        }),
      )
    } finally {
      this.musicFrameInFlight = false
    }
  }

  async getVersionInfo(): Promise<{ version: string; isNewProtocol: boolean }> {
    return await this.withMusicCapturePausedForRead(async () => {
      if (this.magnet != null) {
        await this.magnet.initPromise
      }
      const data = Array(64).fill(0)
      data[0] = 0xe5
      const res = await this.DeviceHID.send(data)
      const version = `${res[1]}.${res[2].toString(16).padStart(2, '0').toUpperCase()}`
      // byte63: 0 = 旧协议，1 = 新协议（固件 >= 1.20）
      const isNewProtocol = res[63] === 1
      this.isNewProtocolCache = isNewProtocol
      return { version, isNewProtocol }
    })
  }

  async getVersion(): Promise<string> {
    return (await this.getVersionInfo()).version
  }

  private decodeFnLayerKey(rawKeyData: number[], dvkToVk: ReadonlyMap<number, number>): DecodedLayerKey | undefined {
    const [byte0 = 0, byte1 = 0, byte2 = 0, byte3 = 0] = rawKeyData
    if (byte0 === 0 && byte1 === 0 && byte2 === 0 && byte3 === 0) {
      return undefined
    }

    // Public driver parses FN bytes as code3, code2, code1, type.
    // Reconstruct action raw codes in that same order: type | code1 | code2 | code3.
    const actionRawCode = (((byte3 << 24) >>> 0) | (byte2 << 16) | (byte1 << 8) | byte0) >>> 0
    const actionKey = getHuochairenFnActionKeyByRawCode(actionRawCode, deviceManager.deviceID)
    if (actionKey !== undefined) {
      return { type: 'key', vk: actionKey as VK }
    }

    if (isHuochairenMacroBindingType(byte3)) {
      return {
        type: 'macro',
        binding: {
          offset: ((byte0 << 8) | byte1) >>> 0,
          repeatCount: byte2,
          macroRawType: byte3 & 0x7f,
        },
      }
    }

    const dvk = (byte0 | (byte1 << 8) | (byte2 << 16) | (byte3 << 24)) >>> 0
    const dvkVk = dvkToVk.get(dvk)
    if (dvkVk !== undefined) {
      return { type: 'key', vk: dvkVk as VK }
    }

    if (byte3 === 0) {
      const standardKeyCandidates = [byte0, byte1, byte2]
      for (let index = standardKeyCandidates.length - 1; index >= 0; index--) {
        const candidate = standardKeyCandidates[index]
        if (candidate === 0 || candidate === 0xff) {
          continue
        }

        const standardVk = this.normalizeFnLayoutStandardKey(candidate, {
          allowDirectVkFallback: true,
        })
        if (standardVk !== undefined) {
          return { type: 'key', vk: standardVk }
        }
      }
    }

    const fallbackVk = this.normalizeFnLayoutStandardKey(byte0, {
      allowDirectVkFallback: true,
    })
    return fallbackVk !== undefined ? { type: 'key', vk: fallbackVk } : undefined
  }

  private async getFnLayerRawKeyMap(): Promise<Map<KK, number[]>> {
    const keyMap = new Map<KK, number[]>()
    const matrixIndexToKK = getHuochairenFnMatrixIndexToKKMap(deviceManager.deviceID)
    const matrixIndexes = Object.keys(matrixIndexToKK).map((index) => Number(index))
    if (matrixIndexes.length === 0) {
      return keyMap
    }

    const profileIndex = this.getCurrentProfileIndex()
    const maxMatrixIndex = Math.max(...matrixIndexes)
    const recordsPerPage = 15
    const pageCount = Math.ceil((maxMatrixIndex + 1) / recordsPerPage)

    for (let pageIndex = 0; pageIndex < pageCount; pageIndex++) {
      const data = Array(64).fill(0)
      data[0] = 0xe2
      data[1] = profileIndex
      data[2] = 0x01
      data[3] = pageIndex

      const res = await this.DeviceHID.send(data)
      for (let recordIndex = 0; recordIndex < recordsPerPage; recordIndex++) {
        const matrixIndex = pageIndex * recordsPerPage + recordIndex
        if (matrixIndex > maxMatrixIndex) {
          break
        }

        const kk = matrixIndexToKK[matrixIndex]
        if (kk === undefined) {
          continue
        }

        const payloadStart = 4 + recordIndex * 4
        keyMap.set(kk as KK, res.slice(payloadStart, payloadStart + 4))
      }
    }

    return keyMap
  }

  private async getNormalLayerRawKeyMap(): Promise<Map<KK, number[]>> {
    const keyMap = new Map<KK, number[]>()
    const matrixIndexToKK = getHuochairenFnMatrixIndexToKKMap(deviceManager.deviceID)
    const matrixIndexes = Object.keys(matrixIndexToKK).map((index) => Number(index))
    if (matrixIndexes.length === 0) {
      return keyMap
    }

    const profileIndex = this.getCurrentProfileIndex()
    const recordsPerPage = 15
    const maxMatrixIndex = Math.max(...matrixIndexes)
    const pageCount = Math.ceil((maxMatrixIndex + 1) / recordsPerPage)

    for (let pageIndex = 0; pageIndex < pageCount; pageIndex++) {
      const data = Array(64).fill(0)
      data[0] = 0xe2
      data[1] = profileIndex
      data[2] = 0x00
      data[3] = pageIndex

      const res = await this.DeviceHID.send(data)
      for (let recordIndex = 0; recordIndex < recordsPerPage; recordIndex++) {
        const matrixIndex = pageIndex * recordsPerPage + recordIndex
        if (matrixIndex > maxMatrixIndex) {
          break
        }

        const kk = matrixIndexToKK[matrixIndex]
        if (kk === undefined) {
          continue
        }

        const payloadStart = 4 + recordIndex * 4
        keyMap.set(kk as KK, res.slice(payloadStart, payloadStart + 4))
      }
    }

    return keyMap
  }

  getCurrentProfileIndex(): number {
    if (this.isNewProtocolCache == false) {
      return 0
    }
    return this.onboardConfigCurrentIndex ?? getCurrentBoundOnboardConfigIndex() ?? 0
  }

  private async getMacroPoolRawData(): Promise<number[]> {
    const profileIndex = this.getCurrentProfileIndex()
    const macroData: number[] = []
    const payloadCapacity = 60
    let totalPages = Math.max(1, Math.ceil(deviceMap[deviceManager.deviceID].support.Limit.MacroBytesLen / payloadCapacity))

    for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
      const data = Array(64).fill(0)
      data[0] = 0xe3
      data[1] = profileIndex
      data[2] = 0x00
      data[3] = pageIndex

      const res = await this.DeviceHID.send(data)
      const deviceTotalPages = res[3] ?? 0
      if (deviceTotalPages > totalPages) {
        totalPages = deviceTotalPages
      }

      macroData.push(...res.slice(4, 4 + payloadCapacity))
    }

    return macroData
  }

  private resolveNormalLayerStandardKey(rawCode: number): VK | undefined {
    if (rawCode === 0 || rawCode === 0xff) {
      return undefined
    }

    const normalizedVk = this.normalizeFnLayoutStandardKey(rawCode, {
      allowDirectVkFallback: true,
    })
    if (normalizedVk !== undefined) {
      return normalizedVk
    }

    try {
      return vkFromKK(rawCode as KK, deviceManager.deviceID)
    } catch (error) {
      void error
    }

    return undefined
  }

  private decodeNormalLayerKey(rawKeyData: number[], dvkToVk: ReadonlyMap<number, number>): { type: 'key'; vk: VK } | { type: 'macro'; binding: DeviceMacroBinding } | undefined {
    const [byte0 = 0, byte1 = 0, byte2 = 0, byte3 = 0] = rawKeyData
    if (byte0 === 0 && byte1 === 0 && byte2 === 0 && byte3 === 0) {
      return undefined
    }

    const actionRawCode = (((byte3 << 24) >>> 0) | (byte2 << 16) | (byte1 << 8) | byte0) >>> 0
    const actionKey = getHuochairenFnActionKeyByRawCode(actionRawCode, deviceManager.deviceID)
    if (actionKey !== undefined) {
      return { type: 'key', vk: actionKey as VK }
    }

    if (isHuochairenMacroBindingType(byte3)) {
      return {
        type: 'macro',
        binding: {
          offset: ((byte0 << 8) | byte1) >>> 0,
          repeatCount: byte2,
          macroRawType: byte3 & 0x7f,
        },
      }
    }

    if (byte3 === 0x04 || byte3 === 0x07) {
      const dvk = (byte0 | (byte1 << 8) | (byte2 << 16) | (byte3 << 24)) >>> 0
      const vk = dvkToVk.get(dvk)
      if (vk !== undefined) {
        return { type: 'key', vk: vk as VK }
      }
    }

    const standardKey = this.resolveNormalLayerStandardKey(byte0)
    if (standardKey !== undefined) {
      return { type: 'key', vk: standardKey }
    }

    const fallbackCandidates = [byte1, byte2]
    for (const candidate of fallbackCandidates) {
      const fallbackKey = this.resolveNormalLayerStandardKey(candidate)
      if (fallbackKey !== undefined) {
        return { type: 'key', vk: fallbackKey }
      }
    }

    return undefined
  }

  private decodeMacroRecordsByOffset(rawData: number[], startOffset: number): Record[] | undefined {
    if (startOffset < 0 || startOffset >= rawData.length) {
      return undefined
    }

    const records: Record[] = []
    let pendingDelay = 0

    for (let index = startOffset; index + 3 < rawData.length; index += 4) {
      const byte0 = rawData[index] ?? 0
      const byte1 = rawData[index + 1] ?? 0
      const byte2 = rawData[index + 2] ?? 0
      const byte3 = rawData[index + 3] ?? 0

      if (byte0 === 0 && byte1 === 0 && byte2 === 0 && byte3 === 0) {
        break
      }

      if (byte0 === 0 && byte1 === 0 && byte2 === 0xc0 && byte3 === 0) {
        break
      }

      const recordType = (byte0 >> 6) & 0x03
      if (recordType === 0x01) {
        pendingDelay += (((byte0 & 0x6f) << 24) | (byte1 << 16) | (byte2 << 8) | byte3) >>> 0
        continue
      }

      if (recordType === 0x02) {
        return undefined
      }

      if (recordType !== 0x00) {
        return undefined
      }

      const delayValue = (((byte0 & 0x3f) << 8) | byte1) + pendingDelay
      pendingDelay = 0

      const keyType = byte2 & 0x3f
      const isRelease = (byte2 & 0x80) !== 0
      const isEnd = (byte2 & 0x40) !== 0

      let vk: VK | undefined
      if (keyType === 0x03) {
        vk = byte3 as VK
      } else {
        vk = VKFromHID(byte3)
        if (vk === undefined) {
          vk = this.normalizeFnLayoutStandardKey(byte3, {
            allowDirectVkFallback: true,
          })
        }
      }

      if (vk === undefined) {
        return undefined
      }

      records.push({
        Key: vk,
        Press: !isRelease,
        Delay: delayValue,
      })

      if (isEnd) {
        break
      }
    }

    return records.length > 0 ? records : undefined
  }

  // isNewProtocol = true: 固件 >= 1.20，macroRawType 2|3 → 持续触发；
  // isNewProtocol = false: 旧固件，macroRawType 1 → 持续触发，2 → 按住触发。
  private decodeMacroBindingType(binding: DeviceMacroBinding, isNewProtocol: boolean): MacroType {
    if (isNewProtocol) {
      if (binding.macroRawType === 2 || binding.macroRawType === 3) {
        return { LoopType: 2, LoopTimes: 2 }
      }
    } else {
      if (binding.macroRawType === 1) {
        return { LoopType: 2, LoopTimes: 2 }
      }
      if (binding.macroRawType === 2) {
        return { LoopType: 3, LoopTimes: 2 }
      }
    }

    if (binding.repeatCount > 0) {
      return { LoopType: 1, LoopTimes: binding.repeatCount + 1 }
    }

    return { LoopType: 0, LoopTimes: 2 }
  }

  private getAdvancedMagnetSourceKeys(): Set<KK> {
    const keys = new Set<KK>()
    this.save.Keys.forEach((key) => {
      if (!key.Input.WithFN && key.Output.Mode === OutputMode.Magnet) {
        keys.add(key.Input.KK)
      }
    })
    return keys
  }

  private isFactoryDefaultNormalRawKey(kk: KK, rawKeyData: number[]): boolean {
    const [byte0 = 0, byte1 = 0, byte2 = 0, byte3 = 0] = rawKeyData
    return byte0 === kk && byte1 === 0 && byte2 === 0 && byte3 === 0
  }

  private isFactoryDefaultNormalKey(kk: KK, vk: VK): boolean {
    try {
      return vkFromKK(kk, deviceManager.deviceID) === vk
    } catch (error) {
      void error
      return false
    }
  }

  private buildNormalLayerLayoutFromSave(): LayerLayoutOverlay {
    const keyMap = new Map<KK, number>()
    const keyMapModifier = new Map<KK, number[]>()
    const keyMapFire = new Map<KK, number[]>()
    const macros = new Map<KK, Record[]>()
    const macroType = new Map<KK, MacroType>()

    this.save.Keys.forEach((key) => {
      if (key.Input.WithFN) {
        return
      }

      const kk = key.Input.KK
      if (key.Output.Mode === OutputMode.Key) {
        if (key.Input.WithModifier.length > 0) {
          keyMapModifier.set(kk, [...key.Input.WithModifier, key.Output.VK])
        } else {
          keyMap.set(kk, key.Output.VK)
        }
        return
      }

      if (key.Output.Mode === OutputMode.Fire) {
        keyMapFire.set(kk, [...key.Output.Extension])
        return
      }

      if (key.Output.Mode === OutputMode.Macro) {
        const records = this.save.Macros.get(key.Output.DataIndex)
        if (records !== undefined) {
          macros.set(kk, records)
          macroType.set(kk, {
            LoopType: key.Output.Extension[0],
            LoopTimes: key.Output.Extension[1],
          })
        }
      }
    })

    return {
      KeyMap: keyMap,
      KeyMapModifier: keyMapModifier,
      KeyMapFire: keyMapFire,
      Macros: macros,
      MacroType: macroType,
    }
  }

  private buildFnLayerLayoutFromSave(): LayerLayoutOverlay {
    const keyMap = new Map<KK, number>()
    const keyMapModifier = new Map<KK, number[]>()
    const keyMapFire = new Map<KK, number[]>()
    const macros = new Map<KK, Record[]>()
    const macroType = new Map<KK, MacroType>()

    this.save.Keys.forEach((key) => {
      if (!key.Input.WithFN) {
        return
      }

      const kk = key.Input.KK
      if (key.Output.Mode === OutputMode.Key) {
        if (key.Input.WithModifier.length > 0) {
          keyMapModifier.set(kk, [...key.Input.WithModifier, key.Output.VK])
        } else {
          keyMap.set(kk, key.Output.VK)
        }
        return
      }

      if (key.Output.Mode === OutputMode.Fire) {
        keyMapFire.set(kk, [...key.Output.Extension])
        return
      }

      if (key.Output.Mode === OutputMode.Macro) {
        const records = this.save.Macros.get(key.Output.DataIndex)
        if (records !== undefined) {
          macros.set(kk, records)
          macroType.set(kk, {
            LoopType: key.Output.Extension[0],
            LoopTimes: key.Output.Extension[1],
          })
        }
      }
    })

    return {
      KeyMap: keyMap,
      KeyMapModifier: keyMapModifier,
      KeyMapFire: keyMapFire,
      Macros: macros,
      MacroType: macroType,
    }
  }

  private clearNormalLayerLayoutEntry(kk: KK, layout: LayerLayoutOverlay) {
    layout.KeyMap.delete(kk)
    layout.KeyMapModifier.delete(kk)
    layout.KeyMapFire.delete(kk)
    layout.Macros.delete(kk)
    layout.MacroType.delete(kk)
  }

  private setMacroLayoutEntry(kk: KK, binding: DeviceMacroBinding, layout: LayerLayoutOverlay, records?: Record[]) {
    this.clearNormalLayerLayoutEntry(kk, layout)
    layout.Macros.set(kk, records ?? [])
    layout.MacroType.set(kk, this.decodeMacroBindingType(binding, this.supportsOnboardConfigs()))
  }

  private applyMacroBindingsToLayout(layout: LayerLayoutOverlay, macroBindings: Map<KK, DeviceMacroBinding>, macroRawData: number[]) {
    const decodedMacros = new Map<number, Record[] | undefined>()

    for (const [kk, binding] of macroBindings) {
      if (!decodedMacros.has(binding.offset)) {
        decodedMacros.set(binding.offset, this.decodeMacroRecordsByOffset(macroRawData, binding.offset))
      }

      this.setMacroLayoutEntry(kk, binding, layout, decodedMacros.get(binding.offset))
    }
  }

  private applyMacroBindingPlaceholders(layout: LayerLayoutOverlay, macroBindings: Map<KK, DeviceMacroBinding>) {
    for (const [kk, binding] of macroBindings) {
      this.setMacroLayoutEntry(kk, binding, layout)
    }
  }

  async getKeyLayoutOverlay(): Promise<Partial<Layout>> {
    return await this.withMusicCapturePausedForRead(async () => {
      const layout = this.buildNormalLayerLayoutFromSave()
      const macroBindings = new Map<KK, DeviceMacroBinding>()
      const dvkToVk = getHuochairenFnVkByDvk(deviceManager.deviceID)
      let magnetLayout: Map<KK, SetMagnetSenior> | undefined
      try {
        magnetLayout = await this.magnet?.getSeniorKeyLayoutOverlay()
      } catch (error) {
        console.warn('get huochairen magnet layout overlay failed', error)
      }

      const advancedMagnetKeys = magnetLayout !== undefined ? new Set<KK>(magnetLayout.keys()) : this.getAdvancedMagnetSourceKeys()
      const rawKeyMap = await this.getNormalLayerRawKeyMap()

      rawKeyMap.forEach((rawKeyData, kk) => {
        if (advancedMagnetKeys.has(kk)) {
          return
        }

        if (this.isFactoryDefaultNormalRawKey(kk, rawKeyData)) {
          this.clearNormalLayerLayoutEntry(kk, layout)
          return
        }

        const decoded = this.decodeNormalLayerKey(rawKeyData, dvkToVk)
        if (decoded === undefined) {
          return
        }

        if (decoded.type === 'key') {
          if (this.isFactoryDefaultNormalKey(kk, decoded.vk)) {
            this.clearNormalLayerLayoutEntry(kk, layout)
            return
          }

          this.clearNormalLayerLayoutEntry(kk, layout)
          layout.KeyMap.set(kk, decoded.vk)
          return
        }

        macroBindings.set(kk, decoded.binding)
      })

      if (macroBindings.size > 0) {
        try {
          const macroRawData = await this.getMacroPoolRawData()
          this.applyMacroBindingsToLayout(layout, macroBindings, macroRawData)
        } catch (error) {
          console.warn('get huochairen macro pool overlay failed', error)
          this.applyMacroBindingPlaceholders(layout, macroBindings)
        }
      }

      const overlay: Partial<Layout> = {
        KeyMap: layout.KeyMap,
        KeyMapModifier: layout.KeyMapModifier,
        Macros: layout.Macros,
        MacroType: layout.MacroType,
        KeyMapFire: layout.KeyMapFire,
      }

      if (magnetLayout !== undefined) {
        overlay.Magenet = magnetLayout
      }

      return overlay
    })
  }

  async getFnKeyMapOverlay(): Promise<Map<KK, number>> {
    return await this.withMusicCapturePausedForRead(async () => {
      const keyMap = new Map<KK, number>()
      const rawKeyMap = await this.getFnLayerRawKeyMap()
      const dvkToVk = getHuochairenFnVkByDvk(deviceManager.deviceID)

      rawKeyMap.forEach((rawKeyData, kk) => {
        // [kk,0,0,0] 是固件对「无出厂 FN 功能键」的出厂状态编码，视为穿透跳过
        if (this.isFactoryDefaultNormalRawKey(kk, rawKeyData)) {
          return
        }
        const decoded = this.decodeFnLayerKey(rawKeyData, dvkToVk)
        if (decoded?.type === 'key') {
          keyMap.set(kk, decoded.vk)
        }
      })

      return keyMap
    })
  }

  async getFnLayoutOverlay(): Promise<Partial<Layout> | undefined> {
    return await this.withMusicCapturePausedForRead(async () => {
      const layout = this.buildFnLayerLayoutFromSave()
      const macroBindings = new Map<KK, DeviceMacroBinding>()
      const rawKeyMap = await this.getFnLayerRawKeyMap()
      const dvkToVk = getHuochairenFnVkByDvk(deviceManager.deviceID)

      rawKeyMap.forEach((rawKeyData, kk) => {
        // [kk,0,0,0] 是固件对「无出厂 FN 功能键」的出厂状态编码，视为穿透跳过
        if (this.isFactoryDefaultNormalRawKey(kk, rawKeyData)) {
          return
        }
        const decoded = this.decodeFnLayerKey(rawKeyData, dvkToVk)
        if (decoded === undefined) {
          return
        }

        if (decoded.type === 'key') {
          this.clearNormalLayerLayoutEntry(kk, layout)
          layout.KeyMap.set(kk, decoded.vk)
          return
        }

        macroBindings.set(kk, decoded.binding)
      })

      if (macroBindings.size > 0) {
        try {
          const macroRawData = await this.getMacroPoolRawData()
          this.applyMacroBindingsToLayout(layout, macroBindings, macroRawData)
        } catch (error) {
          console.warn('get huochairen fn macro pool overlay failed', error)
          this.applyMacroBindingPlaceholders(layout, macroBindings)
        }
      }

      return {
        KeyMap: layout.KeyMap,
        KeyMapModifier: layout.KeyMapModifier,
        Macros: layout.Macros,
        MacroType: layout.MacroType,
        KeyMapFire: layout.KeyMapFire,
      }
    })
  }

  get fnFactoryReset(): FnFactoryResetProvider | undefined {
    if (!hasHuochairenDefaultFnLayout(deviceManager.deviceID)) return undefined
    return {
      resetToDefault: async (kk) => {
        // 有出厂默认的键写回出厂 VK；无出厂默认的键写 defaultVK（data[3]=kk），
        // 与 origin/main syncKeys 的行为一致，固件将 [kk,0,0,0] 解释为「恢复出厂默认/穿透」。
        // 读回时 getFnKeyMapOverlay / getFnLayoutOverlay 会用 isFactoryDefaultNormalRawKey 过滤掉该 pattern。
        // delay(500) 与 syncKeys 的额外等待保持一致，确保固件完成写入后再允许读回，避免第一次读取返回旧值。
        const defaultFnVk = getHuochairenDefaultFnVk(kk, deviceManager.deviceID) ?? defaultVK
        await this.withMusicCapturePausedForWrite(async () => {
          await this.writeSingleKey(kk, defaultFnVk, true)
          await delay(500)
        })
        return true
      },
    }
  }

  getSupportedDeviceFunctionKeys(): VK[] {
    return getHuochairenSupportedDeviceFunctionActionKeys(deviceManager.deviceID) as VK[]
  }

  supportsOnboardConfigs(): boolean {
    // 直接读取 firmware.firmwareVersion（Vue ref），让此处的同步判定被 Vue
    // 模板依赖追踪，固件版本异步填充后能自动触发重新评估；
    // 同时回填 onboardConfigSupportCache，避免后续 ensureOnboardConfigSupported
    // 再走一次异步 getVersion() 通讯。
    if (this.onboardConfigSupportCache === true) {
      return true
    }
    // byte63 新协议标志（由 getVersionInfo 缓存），直接代表固件 >= 1.20
    if (this.isNewProtocolCache === true) {
      this.onboardConfigSupportCache = true
      return true
    }
    const fwVersion = firmware.firmwareVersion.value
    if (fwVersion != null && compare(fwVersion, HUOCHAIREN_ONBOARD_CONFIG_MIN_VERSION, '>=')) {
      this.onboardConfigSupportCache = true
      return true
    }
    return false
  }

  async getOnboardConfigList() {
    const current = await this.getCurrentOnboardConfigIndex()
    return {
      Current: current,
      List: Array.from({ length: HUOCHAIREN_ONBOARD_CONFIG_COUNT }, (_, index) => ({
        Index: index,
        Name: `Config ${index + 1}`,
        Active: current === index,
      })),
    }
  }

  async getCurrentOnboardConfigIndex(): Promise<number> {
    // 缓存命中：无需进入 traffic protection，直接返回
    if (this.onboardConfigCurrentIndex !== null) {
      return this.onboardConfigCurrentIndex
    }

    // 并发去重：多处同时调用（如 ensureBoundOnboardConfig 被并发触发）时，
    // 复用同一 Promise，确保只有一次 HID 命令发向固件。
    // 注意：此检查必须在 withMusicCapturePausedForRead 之前，否则并发调用都能穿透 trafficProtectionDepth 计数器进入内层。
    if (this._currentIndexReadInFlight !== null) {
      return this._currentIndexReadInFlight
    }

    this._currentIndexReadInFlight = (async () => {
      await this.ensureOnboardConfigSupported()

      return await this.withMusicCapturePausedForRead(async () => {
        // 进入 traffic protection 后再次检查缓存，防止等待期间其他路径已写入
        if (this.onboardConfigCurrentIndex !== null) {
          return this.onboardConfigCurrentIndex
        }

        // 首次连接时：优先通过 0xE1 读取 profile 中 ConfigNum 字节，
        // 确保拿到固件实际当前槽位（其他驱动改动过的也能正确回显）。
        const indexFromProfile = await this.readCurrentOnboardConfigIndexFromProfile()
        if (indexFromProfile !== null) {
          this.onboardConfigCurrentIndex = indexFromProfile
          this.rememberOnboardConfigIndex(indexFromProfile)
          return indexFromProfile
        }

        // 兜底：当 0xE1 读取失败时回退到 0x51。
        const res = await this.sendOnboardConfigCommand(false)
        const currentIndex = this.extractOnboardConfigIndex(res)
        if (currentIndex == null) {
          throw new Error(`huochairen onboard config index invalid: ${res[2]}`)
        }

        this.onboardConfigCurrentIndex = currentIndex
        this.rememberOnboardConfigIndex(currentIndex)
        return currentIndex
      })
    })().finally(() => {
      this._currentIndexReadInFlight = null
    })

    return this._currentIndexReadInFlight
  }

  async ensureBoundOnboardConfig(force: boolean = false): Promise<number | null> {
    // force=true 时跳过去重，直接强制执行
    if (!force && this._ensureBoundInFlight !== null) {
      return this._ensureBoundInFlight
    }

    const task = async (): Promise<number | null> => {
      await this.ensureOnboardConfigSupported()

      const boundIndex = getCurrentBoundOnboardConfigIndex()
      if (boundIndex === null) {
        return null
      }

      if (!force) {
        try {
          const currentIndex = await this.getCurrentOnboardConfigIndex()
          if (currentIndex === boundIndex) {
            return currentIndex
          }
        } catch {
          // Fall through to reassert the bound onboard slot.
        }
      }

      await this.setOnboardConfig(boundIndex)
      return boundIndex
    }

    if (!force) {
      this._ensureBoundInFlight = task().finally(() => {
        this._ensureBoundInFlight = null
      })
      return this._ensureBoundInFlight
    }

    return task()
  }

  async setOnboardConfig(index: number): Promise<number> {
    return await this.withMusicCapturePausedForWrite(async () => {
      await this.ensureOnboardConfigSupported()

      const validIndex = this.getValidOnboardConfigIndex(index)
      if (validIndex == null) {
        throw new Error('huochairen onboard config index out of range')
      }

      this.suppressOnboardConfigMonitor()
      await this.sendOnboardConfigCommand(true, validIndex)
      // 写入响应的 data[2] 是固件状态码，不含槽位信息。
      // 直接以写入目标值更新缓存，避免额外发一次 0xE1 读取确认。
      this.onboardConfigCurrentIndex = validIndex
      this.rememberOnboardConfigIndex(validIndex)
      await delay(500)
      return validIndex
    })
  }

  private async turnOnColorCustom() {
    const lightMode = this.save.LightMode
    const data = Array(64).fill(0)
    data[0] = 0x10
    data[1] = this.customMode
    data[2] = 0x09
    data[3] = lightMode.Light
    await this.DeviceHID.send(data, false, true, 8000)
  }
  // 写入自定义颜色
  private async writeCustomColor() {
    const colorsCustom = this.save.ColorsCustom
    const keyMap: Map<KK, number[]> = new Map()
    if (colorsCustom.size == 0) {
      const keys = allKeysForDevice(deviceManager.deviceID)
      keys.forEach((key) => keyMap.set(key, [0, 0, 0]))
    }
    for (const [key, color] of colorsCustom) {
      keyMap.set(key, [(color >> 16) & 0xff, (color >> 8) & 0xff, color & 0xff])
    }
    await this.cmdSet([0x13, 0x01, 0x0f, keyMap.size], keyMap, 200, true, false)
  }
  private _customColorLock = false
  private _customColorPending = false

  async syncCustomColor(): Promise<void> {
    if (this._customColorLock) {
      // 正在执行，标记有新请求
      this._customColorPending = true
      return
    }
    this._customColorLock = true
    try {
      do {
        this._customColorPending = false
        await this.withMusicCapturePausedForWrite(async () => {
          await this.turnOnColorCustom()
          await delay(150)
          await this.writeCustomColor()
        }, 120)
        // 如果执行期间有新请求，则再执行一次（只保留最后一次）
      } while (this._customColorPending)
    } finally {
      this._customColorLock = false
    }
  }

  async syncLeds(): Promise<void> {
    await this.withMusicCapturePausedForWrite(async () => {
      const lightMode = this.save.LightMode
      const data = Array(64).fill(0)
      data[0] = 0x10
      data[1] = lightMode.Mode
      data[2] = lightMode.FullColor ? 0x08 : 0x09
      data[3] = lightMode.Light
      let speed = lightMode.Speed
      if (typeof lightMode.Direction !== 'undefined') {
        if (lightMode.Direction === 1) {
          speed = speed | 0x80
        }
      }
      data[4] = speed

      if (lightMode.FullColor == false) {
        const { r, g, b } = numberToRgb(lightMode.Color)
        data[5] = g
        data[6] = r
        data[7] = b
      }
      await this.DeviceHID.send(data)
      console.log('发送灯光数据:', data)
    }, 120)
  }

  async syncKeys(): Promise<void> {
    await this.withMusicCapturePausedForWrite(async () => {
      await this.magnet?.clearKME()
      const keys = this.save.Keys
      for (const key of keys) {
        if (key.Output.Mode != OutputMode.Key) continue
        await this.writeSingleKey(key.Input.KK, key.Output.VK, key.Input.WithFN)
      }
      await delay(500)
      this.clearResetKeys()
    })
  }

  /**
   * 全量写入所有按键到当前槽位（仅板载同步时调用）。
   *
   * 改键写新值，未改键写 defaultVK（固件解释为恢复出厂），
   * 确保旧槽位通过 CMD 0x20 写入的残留数据被彻底覆盖。
   * FN 层同理，未改键写该键的出厂默认 FN 值。
   */
  private async writeAllKeysToDevice(): Promise<void> {
    await this.withMusicCapturePausedForWrite(async () => {
      const normalMap = new Map<KK, VK>()
      const fnMap = new Map<KK, VK>()
      for (const key of this.save.Keys) {
        if (key.Output.Mode !== OutputMode.Key) continue
        if (key.Input.WithFN) {
          fnMap.set(key.Input.KK, key.Output.VK)
        } else {
          normalMap.set(key.Input.KK, key.Output.VK)
        }
      }
      const allKKs = allKeysForDevice(deviceManager.deviceID)
      for (const kk of allKKs) {
        await this.writeSingleKeyBulk(kk, normalMap.get(kk) ?? defaultVK, false)
      }
      if (hasHuochairenDefaultFnLayout(deviceManager.deviceID)) {
        for (const kk of allKKs) {
          const defaultFnVk = getHuochairenDefaultFnVk(kk, deviceManager.deviceID) ?? defaultVK
          await this.writeSingleKeyBulk(kk, fnMap.get(kk) ?? defaultFnVk, true)
        }
      }
    })
  }

  private buildSingleKeyWriteData(kk: KK, vk: VK, isFn: boolean): number[] {
    const data = Array(64).fill(0)
    data[0] = 0x20
    data[1] = isFn ? 0x01 : 0x00
    data[2] = kk

    if (vk == defaultVK) {
      data[3] = kk
      return data
    }

    if (isDeviceFunctionActionKey(vk)) {
      const rawCode = getHuochairenRawCodeByActionKey(vk, deviceManager.deviceID)
      if (rawCode === undefined) {
        throw new Error(`huochairen device function key is not configured: ${vk}`)
      }

      data[3] = rawCode & 0xff
      data[4] = (rawCode >> 8) & 0xff
      data[5] = (rawCode >> 16) & 0xff
      data[6] = (rawCode >> 24) & 0xff
      return data
    }

    if (defineType.isMedia(vk) || defineType.isMouse(vk) || vk == FNVK) {
      const dvk = DVKFromVK(vk, deviceManager.deviceID)
      data[3] = dvk & 0xff
      data[4] = (dvk >> 8) & 0xff
      data[5] = (dvk >> 16) & 0xff
      data[6] = (dvk >>> 24) & 0xff
    } else {
      const mappedKK = kkFromVK(vk, deviceManager.deviceID)
      if (mappedKK == undefined) {
        data[3] = HIDFromVk(vk) ?? vk
      } else {
        data[3] = mappedKK
      }
    }

    return data
  }

  private async writeSingleKey(kk: KK, vk: VK, isFn: boolean): Promise<void> {
    const data = this.buildSingleKeyWriteData(kk, vk, isFn)
    await this.DeviceHID.send(data)
    await delay(150)
  }

  /**
   * 板载初始化专用：逐键发送 CMD 0x20，不等待每包回包（fire-and-forget），仅保留短间隔以防固件丢包。
   * writeAllKeysToDevice 用于清除旧槽位残留数据，其正确性由后续 syncKeys() 兜底，
   * 无需对每包响应做确认，故可跳过响应等待以大幅缩短初始同步耗时
   * （逐包等待 + 150ms 延迟对 168 键约需 25s，超过 handleCfgSwitch 30s 超时阈值）。
   */
  private async writeSingleKeyBulk(kk: KK, vk: VK, isFn: boolean): Promise<void> {
    const data = this.buildSingleKeyWriteData(kk, vk, isFn)
    await this.DeviceHID.send(data, false, false)
    await delay(40)
  }
  private macroTypeConfig(params: KIO): [number, number] {
    const ext = params.Output.Extension || []
    const mode = (ext[0] ?? 0) & 0xff
    const value = (ext[1] ?? 0) & 0xff

    let repeatCount = 0
    let macroType = mode

    // mode = 0：不循环
    if (mode === 0) {
      repeatCount = 0
    }
    // mode = 1：固定次数循环（value 表示次数，从 1 开始）
    if (mode === 1) {
      macroType = 0
      repeatCount = (value - 1) & 0xff
    }
    return [repeatCount & 0xff, macroType & 0xff]
  }

  // 组装宏数据
  private buildMacroConfig(): [number[], Map<KK, MacroConfig>, Map<KK, MacroConfig>] {
    const macros = this.save.Macros // Map<number, Record[]>
    const keys = this.save.Keys
    const macroMap: Map<KK, MacroConfig> = new Map()
    const macroData: number[] = []
    let actionCount = 0
    const fn_macroMap: Map<KK, MacroConfig> = new Map()
    for (const item of keys) {
      if (item.Output.Mode != OutputMode.Macro) continue

      const macro = macros.get(item.Output.DataIndex)
      if (!macro || macro.length == 0) continue

      const params: number[] = []
      for (let i = 0; i < macro.length; i++) {
        const rec = macro[i]
        const delay = rec.Delay ?? 0

        let keyType = 0x02 // 普通按键
        let hid = HIDFromVk(rec.Key) ?? rec.Key
        if (defineType.isModifier(rec.Key)) keyType = 0x01
        if (defineType.isMouse(rec.Key)) keyType = 0x03

        if (!rec.Press) keyType |= 0x80
        if (i === macro.length - 1) keyType |= 0xc0

        // 具体延时时间==((延时1&0x6F)<<24+延时2<<16+类型<<8+HID)ms
        // 单个延时code最大延时时间 0x6FFF*1MS = 28671ms
        params.push(
          (delay >> 8) & 0x6f, // 延时高7位
          delay & 0xff, // 延时低8位
          keyType & 0xff, // 类型
          hid & 0xff, // HID
        )
        if (defineType.isMouse(rec.Key) && i === macro.length - 1) {
          // TODO:假如宏的最后一个动作为鼠标光标数据，则需要再补一个结束宏：0x00 0x00 0xC0 0x00
          params.push(0x00, 0x00, 0xc0, 0x00)
        }
      }
      macroData.push(...params)
      const [repeatCount, macroType] = this.macroTypeConfig(item)
      const config: MacroConfig = {
        actionCount: actionCount,
        repeatCount: repeatCount,
        macroType: macroType,
      }
      if (item.Input.WithFN) {
        fn_macroMap.set(item.Input.KK, config)
      } else {
        macroMap.set(item.Input.KK, config)
      }
      actionCount += params.length
    }
    return [macroData, macroMap, fn_macroMap]
  }
  // 写入宏数据
  private async writeMacroData(macro: number[]) {
    const payloadCapacity = 64 - 4
    const totalPages = Math.ceil(macro.length / payloadCapacity)
    for (let page = 0; page < totalPages; page++) {
      const data = Array(64).fill(0)
      data[0] = 0x60 // CMD
      data[1] = page // 当前页序号/偏移
      data[2] = totalPages // 总页数

      const offset = page * payloadCapacity
      const chunk = macro.slice(offset, offset + payloadCapacity)
      for (let i = 0; i < chunk.length; i++) {
        data[4 + i] = chunk[i]
      }
      await this.DeviceHID.send(data)
      await delay(50)
    }
  }

  // 按键设置宏
  private async setMacroForKeys(macroMap: Map<KK, MacroConfig>, isFn: boolean) {
    const versionInfo = await this.getVersionInfo()
    for (const [key, item] of macroMap) {
      const data = Array(64).fill(0)
      data[0] = 0x20
      data[1] = isFn ? 0x01 : 0x00
      data[2] = key

      data[3] = (item.actionCount >> 8) & 0xff // 动作总数
      data[4] = item.actionCount & 0xff

      data[5] = item.repeatCount

      if (compare(versionInfo.version, HUOCHAIREN_CANCEL_DEADZONE_V120_MIN_VERSION, '>=') && item.macroType == 0) {
        item.macroType = 1
      }
      data[6] = 0x80 | item.macroType
      await this.DeviceHID.send(data)
      await delay(100)
    }
  }

  async syncMacros(): Promise<void> {
    await this.withMusicCapturePausedForWrite(async () => {
      await this.magnet?.clearKME()

      const [macroData, macroMap, fn_macroMap] = this.buildMacroConfig()
      await this.writeMacroData(macroData)
      // TODO:普通层和fn 层需要分开设置，否则数据会错乱
      await this.setMacroForKeys(macroMap, false)
      await this.setMacroForKeys(fn_macroMap, true)

      await delay(500)
    })
  }
  async syncConfig(): Promise<void> {
    await this.setAllSpecificItem()
  }
  // 优化后的 setAllSpecificItem，只需同步一次
  private async setAllSpecificItem() {
    const items = this.save.Items
    if (items.size === 0) return
    const entry = items.entries().next()
    if (entry.done) return
    const [key, item] = entry.value
    await this.setSpecificItem(key, item)
  }

  getDeviceState(): Promise<ResDeviceState> {
    throw new Error('Method not implemented.')
  }

  async reset(): Promise<void> {
    await this.doReset({ isSlotSwitch: false })
  }

  /**
   * @param isSlotSwitch 为 true 时表示由切换板载槽位触发，data[7] 使用 0x00；
   *                     为 false 时表示用户主动重置，data[7] 使用 0x01。
   */
  private async doReset({ isSlotSwitch }: { isSlotSwitch: boolean }): Promise<void> {
    await this.withMusicCapturePausedForWrite(async () => {
      // 1. 开启静默期，屏蔽硬件主动切换信号（reset 期间）
      this.suppressOnboardConfigMonitor(1500)

      const data = Array(64).fill(0)
      data[0] = 0x52
      data[1] = this.getCurrentProfileIndex()
      data[2] = 0x01 // 所有数据恢复出厂(0:失效  1：当前配置全部恢复  2：所有配置全部恢复)
      data[3] = 0xff // (0：失效FF:所有层都恢复，1：win基础层 2：FN1层 3:FN2层 4 FN4层 5：MAC基础层)
      data[4] = 0x01
      data[5] = 0x01
      data[6] = 0x01
      data[7] = isSlotSwitch ? 0x00 : 0x01

      await this.DeviceHID.send(data)

      // 重置后尝试同步最新的板载索引，并将当前选中的配置同步到板载，防止状态断裂
      // 注意：仅当固件版本支持板载配置（1.20 及以上）时执行
      try {
        if (await this.isOnboardConfigSupportedByFirmware()) {
          await delay(200)
          const boundIndex = this.getBoundOnboardConfigIndex()
          if (boundIndex !== null) {
            await this.setOnboardConfig(boundIndex)
          } else {
            const currentIndex = await this.getCurrentOnboardConfigIndex()
            this.rememberOnboardConfigIndex(currentIndex)
          }
        }
      } catch (e) {
        console.warn('huochairen: failed to sync index after reset', e)
      }
    }, 260)
  }

  private getValFromItems(item: SpecificItem, key: SpecificItem, value: any): any {
    const items = this.save.Items
    return key == item ? value : items.get(key)
  }

  private async isCancelDefaultDeadZoneProtocolV120(): Promise<boolean> {
    if (this.cancelDefaultDeadZoneV120Promise == null) {
      this.cancelDefaultDeadZoneV120Promise = this.getVersionInfo()
        .then(({ version, isNewProtocol }) => isNewProtocol || compare(version, HUOCHAIREN_CANCEL_DEADZONE_V120_MIN_VERSION, '>='))
        .catch((error) => {
          console.warn('huochairen: failed to get firmware version, fallback to v120 cancel default deadzone protocol (legacy path, fw < 1.20)', error)
          return false
        })
    }

    return this.cancelDefaultDeadZoneV120Promise
  }

  private async encodeCancelDefaultDeadZoneValue(value: number | undefined): Promise<number> {
    const normalizedValue = value ? 1 : 0
    if (normalizedValue === 0) {
      return 0
    }
    // v1.20+ 固件使用 0x53 命令设置死区时，开启死区的值为 255（而非 1），但 v1.18 仍为 1。
    const useV120Protocol = await this.isCancelDefaultDeadZoneProtocolV120()
    return useV120Protocol ? 255 : 1
  }

  private decodeCancelDefaultDeadZoneValue(value: number | undefined): number {
    return value && value > 0 ? 1 : 0
  }

  // 固件 1.20+ 使用 0x53 命令分别设置顶部/底部防断触死区
  private async sendCancelDefaultDeadZoneV120Command(bottomValue: number | undefined, topValue: number | undefined): Promise<void> {
    const bottomDeadzone = bottomValue ? HUOCHAIREN_CANCEL_DEADZONE_DEFAULT_VALUE : 0
    const topDeadzone = topValue ? HUOCHAIREN_CANCEL_DEADZONE_DEFAULT_VALUE : 0
    const data = Array(64).fill(0)
    data[0] = 0x53
    data[1] = this.getCurrentProfileIndex()
    data[2] = bottomDeadzone // 底部死区
    data[3] = topDeadzone // 顶部死区
    const res = await this.DeviceHID.send(data)
  }

  // 修复后的 setSpecificItem，从 items 中读取全量数据
  private async setSpecificItem(item: SpecificItem, value: any) {
    // v1.20+ 独立死区项：仅发 0x53，跳过 0x50 全量命令
    if (item === SpecificItem.CancelDefaultBottomDeadZone || item === SpecificItem.CancelDefaultTopDeadZone) {
      if (await this.isCancelDefaultDeadZoneProtocolV120()) {
        await this.withMusicCapturePausedForWrite(async () => {
          // 先从键盘读取两侧当前值，再用新 value 覆盖当前修改侧后一起下发 0x53
          const { bottom: currentBottom, top: currentTop } = await this.serializedE1Read(async () => {
            const readData = Array(64).fill(0)
            readData[0] = 0xe1
            readData[1] = this.getCurrentProfileIndex()
            readData[3] = MG63KeyboardParams.CancelDefaultDeadZone
            const res = await this.DeviceHID.send(readData)
            return {
              bottom: this.decodeCancelDefaultDeadZoneValue(res[44]),
              top: this.decodeCancelDefaultDeadZoneValue(res[45]),
            }
          })
          const bottomValue = item === SpecificItem.CancelDefaultBottomDeadZone ? value : currentBottom
          const topValue = item === SpecificItem.CancelDefaultTopDeadZone ? value : currentTop
          await this.sendCancelDefaultDeadZoneV120Command(bottomValue, topValue)
          await delay(50)
        }, 120)
        return
      }
      // v1.20 以下不支持独立死区项，回退为旧式 CancelDefaultDeadZone 路径
      item = SpecificItem.CancelDefaultDeadZone
    }

    await this.withMusicCapturePausedForWrite(async () => {
      const data = Array(64).fill(0)
      data[0] = 0x50

      // byte1: 回报率
      // PollingRate 未在 items 中配置时（如仅配置了死区等其他项），不能用初始值 1（对应 8000Hz）覆盖设备当前回报率。
      // 此时通过 readSpecificItem 从设备读取当前值，保持原值不变。
      let pollingRate = this.getValFromItems(item, SpecificItem.PollingRate, value)
      if (pollingRate === undefined) {
        try {
          pollingRate = await this.readSpecificItem(SpecificItem.PollingRate)
        } catch {
          pollingRate = 1000 // fallback: 读取失败时保守使用 1000Hz
        }
      }
      let setIndex = 4 // 默认 1000Hz (index=4)，防止 map 无匹配时写错值
      mg63ReportMap.forEach((v, index) => {
        if (v == pollingRate) setIndex = index
      })
      data[1] = setIndex

      data[2] = this.getValFromItems(item, SpecificItem.SleepTime, value) ?? 0xff // 一级休眠
      data[3] = this.getValFromItems(item, SpecificItem.SleepTime, value) ?? 0xff // 一级休眠
      data[4] = 0 // 系统设置默认 win

      data[5] = this.getValFromItems(item, SpecificItem.LockWin, value) || 0
      data[6] = this.getValFromItems(item, SpecificItem.LockKeyboard, value) || 0
      data[7] = this.getValFromItems(item, SpecificItem.DirectionKeySwap, value) || 0
      data[8] = this.getValFromItems(item, SpecificItem.KeyMode, value) || 0
      data[9] = this.getValFromItems(item, SpecificItem.BacklightClose, value) || 0
      data[10] = this.getValFromItems(item, SpecificItem.SideLedClose, value) || 0
      data[11] = this.getValFromItems(item, SpecificItem.SideLedClose, value) || 0

      const useV120Protocol = await this.isCancelDefaultDeadZoneProtocolV120()
      if (useV120Protocol) {
        // 1.20+：0x50 中 data[12] 置 0，通过独立 0x53 命令写死区
        data[12] = 0
      } else {
        data[12] = await this.encodeCancelDefaultDeadZoneValue(this.getValFromItems(item, SpecificItem.CancelDefaultDeadZone, value))
      }
      await this.DeviceHID.send(data)

      if (useV120Protocol && item === SpecificItem.CancelDefaultDeadZone) {
        // CancelDefaultDeadZone 旧式路径：bottom/top 同时使用同一个值
        await this.sendCancelDefaultDeadZoneV120Command(value, value)
      }
      await delay(50)
    }, 120)
  }

  /**
   * 通过 0xE1 命令读取 profile 中从 byteOffset 开始的 length 字节（v1.20+ 支持 512 字节全量访问）。
   * 每次 HID 包有效载荷为 60 字节（res[4..63]），超出单包范围时自动分段读取。
   * 响应格式：CMD(0) + 配置编号(1) + 偏移量(2) + 保留(3) + 有效数据(4+)
   *
   * 注意：调用方须在 serializedE1Read 内调用本方法，防止与其他 0xE1 读取交叉。
   */
  private async readProfileBytes(byteOffset: number, length: number): Promise<number[]> {
    const result: number[] = []
    const PAYLOAD = 60 // 每包有效载荷大小
    let remaining = length
    let currentOffset = byteOffset

    while (remaining > 0) {
      const data = Array(64).fill(0)
      data[0] = 0xe1
      data[1] = this.getCurrentProfileIndex()
      data[3] = currentOffset
      const res = await this.DeviceHID.send(data)
      const toRead = Math.min(remaining, PAYLOAD)
      for (let i = 0; i < toRead; i++) {
        result.push(res[4 + i] ?? 0)
      }
      currentOffset += toRead
      remaining -= toRead
    }
    return result
  }

  /**
   * 从固件 profile 中读取完整灯效参数（仅 v1.20+ 固件支持 512 字节全量读取）。
   * v1.20 以下固件返回 null。
   *
   * 读取内容：
   *  - MainLedParams：背光灯效参数，共 19 种模式 × 9 字节（从模式1/流光开始）
   *  - CustomLedModeLium：自定义灯光亮度（1 字节）
   *  - LogoLedParams：Logo 灯效参数，共 LOGO_LED_MODE_COUNT 种 × 9 字节（从模式0/关灯开始）
   */
  async readLightParamsFromProfile(): Promise<LightProfileParams | null> {
    if (!(await this.isCancelDefaultDeadZoneProtocolV120())) {
      return null
    }

    return this.withMusicCapturePausedForRead(async () => {
      return this.serializedE1Read(async () => {
        // 背光灯效参数：19 种模式，每种 9 字节，起始偏移 76
        const mainLedBytes = await this.readProfileBytes(MG63KeyboardParams.MainLedParameter_OFFSET, MAIN_LED_MODE_COUNT * LED_PARAMETER_RAM_SIZE)
        const mainLedParams: LedParameterRam[] = []
        for (let i = 0; i < MAIN_LED_MODE_COUNT; i++) {
          mainLedParams.push(decodeLedParameterRam(mainLedBytes, i * LED_PARAMETER_RAM_SIZE))
        }

        // 自定义灯光亮度：1 字节，偏移 247
        const liumBytes = await this.readProfileBytes(MG63KeyboardParams.CustomLedModeLium_OFFSET, 1)
        const customLedModeLium = liumBytes[0] ?? 0

        // Logo 灯效参数：LOGO_LED_MODE_COUNT 种，每种 9 字节，起始偏移 256
        const logoLedBytes = await this.readProfileBytes(MG63KeyboardParams.LogoLedParameter_OFFSET, LOGO_LED_MODE_COUNT * LED_PARAMETER_RAM_SIZE)
        const logoLedParams: LedParameterRam[] = []
        for (let i = 0; i < LOGO_LED_MODE_COUNT; i++) {
          logoLedParams.push(decodeLedParameterRam(logoLedBytes, i * LED_PARAMETER_RAM_SIZE))
        }

        return { mainLedParams, customLedModeLium, logoLedParams } satisfies LightProfileParams
      })
    })
  }

  private async readSpecificItem(item: SpecificItem) {
    return await this.withMusicCapturePausedForRead(async () => {
      const data = Array(64).fill(0)
      data[0] = 0xe1
      data[1] = this.getCurrentProfileIndex()

      if (item == SpecificItem.PollingRate) {
        data[3] = MG63KeyboardParams.ReportRate_OFFSET
        const res = await this.DeviceHID.send(data)
        return mg63ReportMap.get(res[20]) || 1000
      }

      if (item == SpecificItem.SleepTime) {
        data[3] = MG63KeyboardParams.FirstSleepTime_OFFSET
        const res = await this.DeviceHID.send(data)
        printHex(res)
        console.log('FirstSleepTime_OFFSET', res[37])
        return res[37]
      }

      if (item == SpecificItem.LockWin) {
        data[3] = MG63KeyboardParams.Winlock_OFFSET
        const res = await this.DeviceHID.send(data)
        return res[27]
      }

      if (item == SpecificItem.LockKeyboard) {
        data[3] = MG63KeyboardParams.KBlock_OFFSET
        const res = await this.DeviceHID.send(data)
        return res[28]
      }
      if (item == SpecificItem.DirectionKeySwap) {
        data[3] = MG63KeyboardParams.DirectionKeySwap
        const res = await this.DeviceHID.send(data)
        return res[29]
      }

      if (item == SpecificItem.BacklightClose) {
        data[3] = MG63KeyboardParams.CustomLedEnable_OFFSET
        const res = await this.DeviceHID.send(data)
        return res[33]
      }
      if (item == SpecificItem.SideLedClose) {
        data[3] = MG63KeyboardParams.SideLedMode_OFFSET
        const res = await this.DeviceHID.send(data)
        return res[34]
      }
      if (item == SpecificItem.KeyMode) {
        data[3] = MG63KeyboardParams.KeyMode
        const res = await this.DeviceHID.send(data)
        return res[42]
      }
      if (item == SpecificItem.CancelDefaultDeadZone || item == SpecificItem.CancelDefaultBottomDeadZone || item == SpecificItem.CancelDefaultTopDeadZone) {
        const useV120Protocol = await this.isCancelDefaultDeadZoneProtocolV120()
        // serializedE1Read 保证与 readCurrentOnboardConfigIndexFromProfile 的 0xE1 串行执行，
        // 避免并发响应互相抢占（cross-talk）
        return this.serializedE1Read(async () => {
          data[3] = MG63KeyboardParams.CancelDefaultDeadZone
          const res = await this.DeviceHID.send(data)
          const rawBottom = res[44]
          const rawTop = res[45]
          const decodedBottom = this.decodeCancelDefaultDeadZoneValue(rawBottom)
          const decodedTop = this.decodeCancelDefaultDeadZoneValue(rawTop)
          if (useV120Protocol && item === SpecificItem.CancelDefaultTopDeadZone) {
            return decodedTop
          }
          return decodedBottom
        })
      }
    })
  }

  async setOrReadSpecificItem(item: SpecificItem, isWrite: boolean, value: any): Promise<any> {
    if (isWrite) {
      return this.setSpecificItem(item as SpecificItem, value)
    }
    return this.readSpecificItem(item)
  }
  pair24g(): Promise<number> {
    throw new Error('Method not implemented.')
  }
  updateFirmware(_: number[]): Promise<boolean> {
    throw new Error('Method not implemented.')
  }
  async syncDecorationLed(): Promise<void> {
    const decorates = this.save.Decorates
    if (this.changeProfileFlag && (decorates == undefined || decorates.length == 0)) {
      return
    }

    if (decorates == undefined || decorates.length == 0) {
      throw new Error('Decorates is undefined')
    }
    await this.withMusicCapturePausedForWrite(async () => {
      for (let i = 0; i < decorates.length; i++) {
        const decorate = decorates[i]
        const data = Array(64).fill(0)
        data[0] = 0x11
        data[1] = decorate.LightMode.Mode
        data[2] = decorate.LightMode.FullColor ? 0x08 : 0x09
        data[3] = decorate.LightMode.Light
        let speed = decorate.LightMode.Speed
        if (typeof decorate.LightMode.Direction !== 'undefined') {
          if (decorate.LightMode.Direction === 1) {
            speed = speed | 0x80
          }
        }
        data[4] = speed

        if (decorate.LightMode.FullColor == false) {
          const { r, g, b } = numberToRgb(decorate.LightMode.Color)
          data[5] = g
          data[6] = r
          data[7] = b
        }
        await this.DeviceHID.send(data)
        await delay(150)
      }
    })
  }
  async syncDynamicColor(): Promise<boolean> {
    const colorsDynamic = this.save.ColorsDynamic
    if (this.changeProfileFlag && colorsDynamic.Run == false) {
      return true
    }

    let state = MusicCaptureStatus.Stop
    if (colorsDynamic.Run) {
      // if (!this.save.LightLastIsCustom) {
      //   await this.turnOnColorCustom()
      // }
      state = MusicCaptureStatus.Start
    }
    return await this.setDynamicColorState(state)
  }

  private async setDynamicColorState(state: MusicCaptureStatus) {
    this.musicIns.setColorsDynamic(this.save.ColorsDynamic)
    return await this.musicIns.dynamicColorState(state)
  }

  async cmdSet(cmd: number[], sets: Map<KK, number[]>, delayValue: number = 100, isAwait: boolean = true, needCalculateSetNum: boolean = true) {
    if (sets.size == 0) return new Map<KK, [number, number]>()
    let index = 0
    let prefixed = [...cmd]
    const data: number[][] = [[...prefixed]]
    const counts: number[] = [0]
    sets.forEach((v, k) => {
      if (data[index].length >= 64 - v.length) {
        index++
        data[index] = [...prefixed]
        counts[index] = 0
      }
      data[index].push(k, ...v)
      counts[index]++
    })
    for (let i = 0; i < data.length; i++) {
      if (needCalculateSetNum) {
        data[i][1] = counts[i]
      }
      const toSend = bytesPadding64(data[i], 64, [0x00])
      // printHex(toSend)
      if (isAwait) {
        await this.DeviceHID.send(toSend, false, true, 8100)
        await delay(delayValue)
      } else {
        this.DeviceHID.send(toSend, false, false, 8000)
      }
    }
  }

  async send(data: number[]) {
    return await this.DeviceHID.send(data, false, true, 8000)
  }
  destroy() {
    super.destroy()
  }
}
